<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  nextTick,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import IntersectionObserver from './IntersectionObserver.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import InboxDisconnectBanner from './InboxDisconnectBanner.vue';
import MarkAllReadDialog from './widgets/conversation/MarkAllReadDialog.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useStorage } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useEventListener } from '@vueuse/core';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import ConversationApi from 'dashboard/api/conversations';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  issueId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
  qaFilterCommand: { type: Object, default: null },
  fixedAssigneeType: { type: String, default: '' },
  fixedStatus: { type: String, default: '' },
  pageTitle: { type: String, default: '' },
  hideFilterControls: { type: Boolean, default: false },
  hideCollapseToggle: { type: Boolean, default: false },
  isQAMonitor: { type: Boolean, default: false },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);

provide('contextMenuElementTarget', conversationDynamicScroller);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// Add flag to prevent duplicate fetches
const isFetchingData = ref(false);
// If filters change while a fetch is in-flight, queue a single refetch for the latest state.
const queuedResetKey = ref(null);
const inFlightResetKey = ref(null);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

// Collapsible sidebar state
const isCollapsed = computed(() => {
  return uiSettings.value?.is_chat_list_collapsed || false;
});

const toggleCollapse = () => {
  const newCollapsedState = !isCollapsed.value;
  updateUISettings({
    is_chat_list_collapsed: newCollapsedState,
  });
};

const currentUser = useMapGetter('getCurrentUser');
const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const unreadChatsList = useMapGetter('getUnreadChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  const tabConfig = [
    { key: 'all', stateKey: 'allCount' },
    { key: 'unassigned', stateKey: 'unAssignedCount' },
    { key: 'me', stateKey: 'mineCount' },
    { key: 'unread', stateKey: 'unreadCount' },
  ];
  return tabConfig.map(({ key, stateKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[stateKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return true;
});

const wlabSidebarCollapsed = useStorage(
  'wlab-secondary-sidebar-collapsed',
  false
);

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  ).count;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  // 将 VIP 作为标准 conversationType 传给后端，由后端完成初步筛选
  const apiConversationType = props.conversationType || undefined;
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    issueId: props.issueId || undefined,
    conversationType: apiConversationType,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const isQaMonitorRoute = computed(() => {
  const routeName = String(route.name || '');
  const routePath = String(route.path || '');
  return (
    routeName.startsWith('qa_monitor') || routePath.includes('/qa-monitor')
  );
});

const isQaMonitorMode = computed(
  () => props.isQAMonitor || isQaMonitorRoute.value
);

const isConversationAllContext = computed(() => {
  if (props.fixedStatus || isQaMonitorMode.value) {
    return false;
  }

  const hasScopedConversationFilter =
    Boolean(props.conversationInbox) ||
    Boolean(props.teamId) ||
    Boolean(props.issueId) ||
    Boolean(props.label) ||
    Boolean(props.conversationType) ||
    Boolean(props.foldersId);

  if (hasScopedConversationFilter) {
    return false;
  }

  const routeAssignee = String(
    route.query?.assignee || wootConstants.ASSIGNEE_TYPE.ALL
  ).toLowerCase();

  return routeAssignee === wootConstants.ASSIGNEE_TYPE.ALL;
});

const pageTitle = computed(() => {
  if (props.pageTitle) {
    return props.pageTitle;
  }

  if (isQaMonitorMode.value) {
    return t('SIDEBAR.QA_LIVE_MONITOR');
  }

  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'vip') {
    return t('SIDEBAR.VIP_CONVERSATIONS');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unread') {
      localConversationList = [...unreadChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  // VIP 视图：后端已做初筛，前端再做一次兜底过滤，避免脏数据
  if (props.conversationType === 'vip') {
    const getVipLevel = value => {
      if (!value && value !== 0) return 0;
      if (typeof value === 'string') {
        const match = value.match(/(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      }
      return parseInt(value, 10) || 0;
    };
    localConversationList = localConversationList.filter(conversation => {
      const rawVip = conversation?.meta?.sender?.custom_attributes?.vip;
      return getVipLevel(rawVip) > 0;
    });
  }

  return localConversationList;
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function getConversationFilterByUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  return filterBy;
}

function resolveDefaultConversationStatus() {
  if (props.fixedStatus) {
    return props.fixedStatus;
  }

  // Force Conversation > All to use "Status = All" regardless of saved UI settings.
  if (isConversationAllContext.value) {
    return wootConstants.STATUS_TYPE.ALL;
  }

  const { status } = getConversationFilterByUISettings();
  return status || wootConstants.STATUS_TYPE.OPEN;
}

function syncStatusWithRouteContext() {
  if (props.fixedStatus) return false;

  const nextStatus = resolveDefaultConversationStatus();
  if (!nextStatus || nextStatus === activeStatus.value) return false;

  activeStatus.value = nextStatus;
  store.dispatch('setChatStatusFilter', nextStatus);
  return true;
}

function setFiltersFromUISettings() {
  if (props.fixedStatus) {
    activeStatus.value = props.fixedStatus;
    return;
  }

  const { order_by: orderBy } = getConversationFilterByUISettings();
  activeStatus.value = resolveDefaultConversationStatus();
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  return store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();

  // If we're in a folder view, we need to merge the folder's original query
  // with the new filters, rather than replacing it completely
  let finalPayload = payload;
  if (hasActiveFolders.value && activeFolder.value?.query?.payload) {
    // Combine folder's existing filters with new filters
    const folderFilters = activeFolder.value.query.payload;
    finalPayload = [...folderFilters, ...payload];
  }

  const queryData = filterQueryGenerator(finalPayload);
  foldersQuery.value = queryData;
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(finalPayload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  // Don't clear appliedFilter here - it should persist to show active filters
  // appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    0,
    {},
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  return store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function buildResetKey() {
  // IMPORTANT: do not include pagination in the reset key.
  // Pagination changes during scrolling and list updates, but a reset fetch should
  // be keyed only on the base filters (inbox/team/label/type/status/sort/folder).
  const f = conversationFilters.value || {};
  return JSON.stringify({
    inboxId: f.inboxId || 0,
    assigneeType: f.assigneeType || '',
    status: f.status || '',
    sortBy: f.sortBy || '',
    labels: f.labels || null,
    teamId: f.teamId || null,
    issueId: f.issueId || null,
    conversationType: f.conversationType || null,
    foldersId: props.foldersId || 0,
    activeFolderId: activeFolder.value?.id || null,
    folderQuery: hasActiveFolders.value
      ? activeFolder.value?.query || null
      : null,
  });
}

function resetAndFetchData() {
  const nextResetKey = buildResetKey();

  // Prevent duplicate fetches
  if (isFetchingData.value) {
    // Don't drop updates (eg. switching inbox/channel while a fetch is in-flight).
    // Queue the latest reset state and re-run once current request finishes.
    queuedResetKey.value = nextResetKey;
    return;
  }

  // If foldersId exists but activeFolder is not ready yet, skip this call
  // The watcher will trigger resetAndFetchData again once activeFolder is computed
  if (props.foldersId && props.foldersId !== 0 && !activeFolder.value) {
    console.log(
      'Folder ID exists but folder not loaded yet, waiting for folder to load...'
    );
    return;
  }

  isFetchingData.value = true;
  inFlightResetKey.value = nextResetKey;
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');

  // Determine which fetch function to call based on current state
  let fetchPromise;

  if (hasActiveFolders.value) {
    // Has active folder - use filtered conversations
    const payload = activeFolder.value.query;
    fetchPromise = fetchSavedFilteredConversations(payload);
  } else {
    // No folder filter - fetch all conversations
    fetchPromise = fetchConversations();
  }

  // Always reset the flag after fetch completes or fails
  if (fetchPromise && fetchPromise.finally) {
    fetchPromise.finally(() => {
      isFetchingData.value = false;
      const queued = queuedResetKey.value;
      queuedResetKey.value = null;
      // If something changed during the in-flight fetch (eg. inbox/channel switched),
      // run exactly one more reset for the latest state.
      if (queued && queued !== inFlightResetKey.value) {
        // Clear in-flight key before re-running.
        inFlightResetKey.value = null;
        resetAndFetchData();
      } else {
        inFlightResetKey.value = null;
      }
    });
  } else {
    isFetchingData.value = false;
    inFlightResetKey.value = null;
  }
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Add a method to handle scroll events
function handleScroll() {
  const scroller = conversationDynamicScroller.value;
  if (scroller && scroller.hasScrollbar) {
    const { scrollTop, scrollHeight, clientHeight } = scroller.$el;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMoreConversations();
    }
  }
}

function updateAssigneeTab(selectedTab) {
  if (props.fixedAssigneeType) return;

  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    // Sync query param so sidebar active state and deep links work
    try {
      const nextQuery = { ...route.query };
      if (
        selectedTab === wootConstants.ASSIGNEE_TYPE.ALL ||
        selectedTab === 'all'
      ) {
        delete nextQuery.assignee;
      } else {
        nextQuery.assignee = selectedTab;
      }
      router.replace({
        name: route.name,
        params: route.params,
        query: nextQuery,
      });
    } catch (e) {
      /* noop */
    }
    if (!currentPage.value) {
      fetchConversations();
    }
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    if (props.fixedStatus) return;
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  if (String(route.name || '').startsWith('inbox_view')) {
    router.push({
      name: 'inbox_view',
      params: { accountId: route.params.accountId },
    });
    return;
  }

  const {
    params: { accountId, inbox_id: inboxId, label, teamId, issueId },
  } = route;

  router.push(
    conversationListPageURL({
      accountId,
      conversationType: props.conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
      issueId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  if (isQaMonitorMode.value) return;

  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  if (isQaMonitorMode.value) return;

  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(conversationId, status, snoozedUntil) {
  store
    .dispatch('toggleStatus', {
      conversationId,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    });
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return store.getters.getConversationById(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

useEventListener(conversationDynamicScroller, 'scroll', handleScroll);

onMounted(() => {
  // Ensure dependent stores are ready early (prevents missing channel/labels on first paint)
  try {
    const inboxes = store.getters['inboxes/getInboxes'];
    if (!Array.isArray(inboxes) || inboxes.length === 0) {
      store.dispatch('inboxes/get');
    }
  } catch (e) {
    /* noop */
  }

  try {
    const labels = store.getters['labels/getLabels'];
    if (!Array.isArray(labels) || labels.length === 0) {
      store.dispatch('labels/get');
    }
  } catch (e) {
    /* noop */
  }

  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  if (props.fixedAssigneeType) {
    activeAssigneeTab.value = props.fixedAssigneeType;
  } else {
    // Initialize assignee tab from route query (?assignee=unassigned|me|unread|all)
    try {
      const q = String(route.query?.assignee || '').toLowerCase();
      const allowed = ['unassigned', 'me', 'unread', 'all'];
      const normalized = allowed.includes(q) ? q : 'all';
      if (normalized !== activeAssigneeTab.value) {
        activeAssigneeTab.value = normalized;
      }
    } catch (e) {
      /* noop */
    }
  }
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

// Keep local `activeStatus` in sync when other parts
// of the UI (e.g. "Mark as pending") change the global status filter.
watch(chatStatusFilter, next => {
  if (props.fixedStatus) return;

  const v = (next || '').toString();
  if (!v) return;
  if (v === activeStatus.value) return;
  activeStatus.value = v;
  resetAndFetchData();
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);
const clearUnreadDialogRef = ref(null);
const isClearingUnread = ref(false);

// Mark-all-as-read is dispatched as a Sidekiq background job
// (Conversations::MarkAllReadJob) so requests don't block on accounts with
// 50k+ open conversations. The job can take anywhere from <1s (small account)
// to 30s+ (50k+ rows). Each polling tick fans out 5+ /meta calls to the
// backend (assignee tabs + VIP + Unattended), so we cap polling to two
// progressive ticks. Customers with truly large accounts (>30k unread) may
// need a manual reload to see the final state — acceptable for an action
// that runs once per session.
//   - 3s   catches small / medium accounts
//   - 12s  catches large accounts (~30k conversations)
const MARK_ALL_READ_REFRESH_DELAYS_MS = [3000, 12000];

async function confirmClearAllUnread({ inboxIds } = {}) {
  if (isClearingUnread.value) return;
  isClearingUnread.value = true;
  try {
    await ConversationApi.markAllRead({ inboxIds });
    useAlert(t('CHAT_LIST.CLEAR_ALL_UNREAD_SUCCESS'));

    // Polling refreshes — each refresh MUST trigger replace mode in
    // SET_ALL_CONVERSATION so stale items already marked read on the server
    // are evicted from the local list. Replace mode requires `page === 1`
    // (see buildConversationList in actionHelpers.js).
    //
    // Without the explicit page=1 override below, the polling fetch reuses
    // whatever page value is currently in state.conversationFilters — which
    // is `currentPage + 1` (typically 2 or more after any scroll/load) once
    // the user has loaded the first page. That puts SET_ALL_CONVERSATION
    // into MERGE mode, so already-read conversations stay in the list while
    // the toast says "Success" — the exact bug agents reported.
    MARK_ALL_READ_REFRESH_DELAYS_MS.forEach(delay => {
      setTimeout(async () => {
        const currentFilters =
          store.state?.conversations?.conversationFilters || {};
        await store.dispatch('updateChatListFilters', {
          ...currentFilters,
          page: 1,
        });
        await store.dispatch('fetchAllConversations');
        emitter.emit(BUS_EVENTS.REFRESH_SIDEBAR_UNREAD_COUNTS);
      }, delay);
    });
  } catch {
    useAlert(t('CHAT_LIST.CLEAR_ALL_UNREAD_ERROR'));
  } finally {
    isClearingUnread.value = false;
    clearUnreadDialogRef.value?.close();
  }
}

function handleClearAllUnread() {
  clearUnreadDialogRef.value?.open();
}

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('updateConversationStatus', toggleConversationStatus);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

watch(activeTeam, () => {
  syncStatusWithRouteContext();
  if (hasAppliedFilters.value) return;
  resetAndFetchData();
});

watch(
  computed(() => props.conversationInbox),
  () => {
    syncStatusWithRouteContext();
    if (hasAppliedFilters.value) return;
    resetAndFetchData();
  }
);
watch(
  computed(() => props.label),
  () => {
    syncStatusWithRouteContext();
    if (hasAppliedFilters.value) return;
    resetAndFetchData();
  }
);
watch(
  computed(() => props.conversationType),
  () => {
    syncStatusWithRouteContext();
    if (hasAppliedFilters.value) return;
    resetAndFetchData();
  }
);
watch(
  computed(() => props.issueId),
  () => {
    syncStatusWithRouteContext();
    store.dispatch('updateChatListFilters', conversationFilters.value);
    if (hasAppliedFilters.value) return;
    resetAndFetchData();
  }
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
    syncStatusWithRouteContext();
    // Only reset and fetch if there are no applied filters
    // If filters are applied, they should take precedence
    if (!hasAppliedFilters.value) {
      resetAndFetchData();
    }
  }
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

// FUNDORA fix (display-only): when the rendered list shrinks — e.g. a
// conversation resolves via realtime and the status getter drops it —
// vue-virtual-scroller can keep a stale total-height/spacer, leaving a
// visible empty gap and a ghost (recycled) card under high churn.
// Nudge the scroller to recompute its layout on the next tick whenever
// the rendered count changes. This does NOT clear the size cache (no
// re-measure flash) and does NOT touch the store, realtime handlers,
// or the message send/receive pipeline.
watch(
  () => conversationList.value.length,
  () => {
    nextTick(() => {
      conversationDynamicScroller.value?.forceUpdate?.();
    });
  }
);

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

// Keep assignee tab in sync with route changes (from sidebar)
watch(
  () => route.query?.assignee,
  newVal => {
    if (props.fixedAssigneeType) return;

    const q = String(newVal || '').toLowerCase();
    const allowed = ['unassigned', 'me', 'unread', 'all'];
    const normalized = allowed.includes(q) ? q : 'all';
    const hasTabChanged = normalized !== activeAssigneeTab.value;

    if (hasTabChanged) {
      activeAssigneeTab.value = normalized;
    }

    const hasStatusChanged = syncStatusWithRouteContext();
    if (hasTabChanged || hasStatusChanged) {
      resetAndFetchData();
    }
  }
);

watch(
  () => props.qaFilterCommand,
  command => {
    if (!isQaMonitorMode.value || !command?.type) {
      return;
    }

    if (command.type === 'apply' && Array.isArray(command.payload)) {
      onApplyFilter(command.payload);
      return;
    }

    if (command.type === 'clear') {
      resetAndFetchData();
    }
  }
);
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 bg-n-solid-1 conversations-list-wrap transition-[width] duration-300"
    :class="[
      { hidden: !showConversationList },
      isCollapsed
        ? 'w-16'
        : isOnExpandedLayout
          ? 'basis-full'
          : wlabSidebarCollapsed
            ? 'flex-shrink-0'
            : 'w-[280px] 2xl:w-[320px] max-[1600px]:w-[260px] flex-shrink-0',
      wlabSidebarCollapsed && !isCollapsed && !isOnExpandedLayout
        ? 'sidebar-collapsed-expand'
        : '',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :is-qa-monitor="isQaMonitorMode"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :is-collapsed="isCollapsed"
      :hide-filter-controls="hideFilterControls"
      :hide-collapse-toggle="hideCollapseToggle"
      :active-assignee-tab="activeAssigneeTab"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @toggle-collapse="toggleCollapse"
      @clear-all-unread="handleClearAllUnread"
    />

    <InboxDisconnectBanner
      v-if="conversationInbox && !isCollapsed"
      :inbox-id="conversationInbox"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <!-- Moved assignee tabs (All/Unassigned/Mine/Unread) into the sidebar.
         Hide the legacy in-list tabs to avoid duplication. -->
    <ChatTypeTabs
      v-if="false"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />

    <p
      v-if="!chatListLoading && !conversationList.length && !isCollapsed"
      class="flex items-center justify-center p-4 overflow-auto"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      v-if="selectedConversations.length && !isCollapsed"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      ref="conversationListRef"
      class="flex-1 overflow-y-auto overflow-x-hidden conversations-list"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <DynamicScroller
        ref="conversationDynamicScroller"
        :items="conversationList"
        :min-item-size="24"
        class="w-full h-full"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[
              item.labels,
              item.uuid,
              item.inbox_id,
              item.last_activity_at,
              item.timestamp,
              item.unread_count,
              item.status,
            ]"
          >
            <!-- Wrapper is measured by DynamicScrollerItem; padding creates real gaps -->
            <div class="wlab-conv-list-item">
              <ConversationItem
                :source="item"
                :label="label"
                :team-id="teamId"
                :issue-id="issueId"
                :folders-id="foldersId"
                :conversation-type="conversationType"
                :show-assignee="showAssigneeInConversationCard"
                :is-collapsed="isCollapsed"
                :is-qa-monitor="isQaMonitorMode"
                @select-conversation="selectConversation"
                @de-select-conversation="deSelectConversation"
              />
            </div>
          </DynamicScrollerItem>
        </template>
        <template #after>
          <div v-if="chatListLoading" class="flex justify-center my-4">
            <Spinner class="text-white" />
          </div>
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-n-slate-11"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <IntersectionObserver
            v-else
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </template>
      </DynamicScroller>
    </div>
    <MarkAllReadDialog
      ref="clearUnreadDialogRef"
      :is-loading="isClearingUnread"
      @confirm="confirmClearAllUnread"
    />
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
  </div>
</template>
